import React, { memo } from 'react';
import { OrderRetornoIcon } from './OrderRetornoIcon';
import { isOrderRetornoByRequestStatus } from '../utils/retornoStatus';
import { strTrim, hasDefeitoAlert, normalizeHexColor, defeitoAlertTooltipText } from '../utils/defeitoAlertBadge';
import { getBoardClienteExibicao, getBoardClienteTooltipNome } from '../utils/clienteBoardDisplay';
import {
  resolveBoardDragOrderIds,
  setBoardOrderDragDataTransfer,
  setBoardOrderDragSource,
  clearBoardOrderDragSource,
} from '../utils/boardOrderDrag';
import { IconLoader4, IconPencilExclamation } from '@tabler/icons-react';
import { areBoardOrderRowPropsEqual } from '../utils/boardOrderRowCompare';

function getSLAColor(sla) {
  switch (sla) {
    case 'Vencido':
    case 'vencido':
      return '#ef5350';
    case 'Vencendo':
    case 'vencendo':
      return '#ff9800';
    default:
      return 'transparent';
  }
}

function getServiceIcon(tipo, isPaused) {
  if (isPaused) return 'P';
  return tipo || '?';
}

/** Ciclo nativo a partir do primeiro retorno (ciclo 2). Ciclo 1 não entra na coluna. */
function nativeReturnCycle(ordem) {
  if (ordem?.source !== 'native') return 0;
  const cycle = Math.max(1, Number(ordem.currentCycle) || (Number(ordem.returnCount) || 0) + 1);
  return cycle >= 2 ? cycle : 0;
}

function getServiceColor(tipo, isPaused) {
  if (isPaused) return '#fbbf24';
  switch (tipo) {
    case 'E':
      return '#9c27b0';
    case 'B':
      return '#ff5722';
    case 'A':
      return '#607d8b';
    case 'R':
      return '#795548';
    case 'D':
      return '#f44336';
    case 'I':
      return '#2196f3';
    case 'S':
      return '#4caf50';
    case 'C':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
}

/**
 * Row de OS na coluna do técnico.
 * memo com compare visual (id/sla/selected/retorno/defeito + campos do card).
 */
function TechnicianOrderRow({
  ordem,
  technician,
  groupName,
  selectedOrders = [],
  isSelected,
  setContextMenu,
  handleOrderClick,
  onBoardOrderDragStart,
  onBoardOrderDragEnd,
  showDefeitoColumn = false,
  codstatusRetorno = '',
  codstatusPedidoVinculadoDisponivel = '',
  extraRetornoStatuses = [],
  userShowOSConfig = 0,
  defeitosData = {},
  loadingDefeitos = false,
  onCellTooltip,
}) {
  const sectionName = (ordem && ordem.boardTechnicianSection) || groupName;
  const isRetornoByRequestStatus = isOrderRetornoByRequestStatus(
    ordem,
    codstatusRetorno,
    extraRetornoStatuses
  );
  const defeitoAlert = hasDefeitoAlert(ordem);
  const defeitoBg = defeitoAlert ? normalizeHexColor(ordem.color_bg) : null;
  const nativeCycle = nativeReturnCycle(ordem);
  const isAguardando = !!ordem.isAguardandoAtendimento;
  const isAssinatura = !!ordem.isAguardandoAssinatura;
  const selected =
    typeof isSelected === 'boolean' ? isSelected : selectedOrders.includes(ordem.id);
  const slaColor = getSLAColor(ordem.sla);
  const equipamentoLabel =
    ordem.equipamento || ordem.TB01010_RESUMIDO || ordem.TB01010_NOME || '';

  let defeitoText = '';
  let equipamentoNode;
  if (showDefeitoColumn) {
    const orderNumber = String(ordem.TB02115_CODIGO || ordem.id || '');
    const defeitoInfo = defeitosData[orderNumber];
    if (loadingDefeitos) {
      equipamentoNode = (
        <div
          className="technician-order-cell technician-order-equipamento board-cell-tooltip-trigger"
          onMouseEnter={(e) => {
            if (equipamentoLabel) {
              const r = e.currentTarget.getBoundingClientRect();
              onCellTooltip?.({
                text: equipamentoLabel,
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }
          }}
          onMouseLeave={() => onCellTooltip?.(null)}
        >
          {equipamentoLabel}
          <span className="defeito-skeleton-inline">
            <span className="skeleton-line-inline" />
          </span>
        </div>
      );
    } else if (defeitoInfo && defeitoInfo.defeito && defeitoInfo.defeito !== 'Não informado') {
      defeitoText = ` (${defeitoInfo.defeito})`;
    }
  }

  if (!equipamentoNode) {
    const tooltipText =
      equipamentoLabel +
      (defeitoText ? ` · Defeito: ${defeitoText.replace(/[()]/g, '').trim()}` : '');
    equipamentoNode = (
      <div
        className="technician-order-cell technician-order-equipamento board-cell-tooltip-trigger"
        onMouseEnter={(e) => {
          if (tooltipText) {
            const r = e.currentTarget.getBoundingClientRect();
            onCellTooltip?.({
              text: tooltipText,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }
        }}
        onMouseLeave={() => onCellTooltip?.(null)}
      >
        {equipamentoLabel}
        {defeitoText ? <span className="defeito-inline">{defeitoText}</span> : null}
      </div>
    );
  }

  return (
    <div
      className={`technician-order-row ${selected ? 'selected' : ''} ${
        isRetornoByRequestStatus ? 'order-status-retorno' : ''
      } ${ordem.pedidoVinculado ? 'has-pedido-vinculado' : ''}${
        defeitoAlert ? ' technician-order-row--defeito-alert' : ''
      }${nativeCycle ? ' technician-order-row--native-cycle' : ''}${isAguardando ? ' technician-order-row--aguardando-atendimento' : ''}${
        isAssinatura ? ' technician-order-row--aguardando-assinatura' : ''
      }`}
      onClick={() => handleOrderClick(ordem, technician, sectionName)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          orderId: ordem.id,
          technician,
          groupName: sectionName,
          source: 'technician',
        });
      }}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        const idsToDrag = resolveBoardDragOrderIds(selectedOrders, ordem.id);
        setBoardOrderDragDataTransfer(e.dataTransfer, ordem.id, idsToDrag);
        e.dataTransfer.setData('fromTechnician', 'true');
        e.dataTransfer.setData('fromTechnicianName', technician);
        e.dataTransfer.setData('fromGroup', sectionName);
        setBoardOrderDragSource({ fromTechnicianName: technician, fromGroup: sectionName });
        onBoardOrderDragStart?.();
      }}
      onDragEnd={() => {
        clearBoardOrderDragSource();
        onBoardOrderDragEnd?.();
      }}
    >
      <div className="technician-order-cell technician-order-id">
        <span
          className={`service-type-badge-compact${
            defeitoAlert ? ' service-type-badge-compact--defeito-alert board-cell-tooltip-trigger' : ''
          }`}
          style={{
            backgroundColor: defeitoBg || getServiceColor(ordem.tipo, ordem.isPausaOficina),
          }}
          onMouseEnter={
            defeitoAlert
              ? (e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  onCellTooltip?.({
                    text: defeitoAlertTooltipText(ordem),
                    x: r.left + r.width / 2,
                    y: r.bottom - 4,
                    triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
                  });
                }
              : undefined
          }
          onMouseLeave={defeitoAlert ? () => onCellTooltip?.(null) : undefined}
        >
          {defeitoAlert
            ? strTrim(ordem.defeito_alert)
            : getServiceIcon(ordem.tipo, ordem.isPausaOficina)}
        </span>
        {slaColor !== 'transparent' ? (
          <div className="sla-indicator" style={{ backgroundColor: slaColor }} />
        ) : null}
        {isAguardando ? (
          <span
            className="technician-order-aguardando-icon board-cell-tooltip-trigger"
            aria-label="Aguardando atendimento"
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              onCellTooltip?.({
                text: 'Aguardando atendimento',
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onCellTooltip?.(null)}
          >
            <IconLoader4 size={12} stroke={2} aria-hidden />
          </span>
        ) : null}
        {isAssinatura ? (
          <span
            className="technician-order-aguardando-icon is-assinatura board-cell-tooltip-trigger"
            aria-label="Aguardando assinatura"
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              onCellTooltip?.({
                text: 'Aguardando assinatura',
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onCellTooltip?.(null)}
          >
            <IconPencilExclamation size={12} stroke={2} aria-hidden />
          </span>
        ) : null}
        <span
          className={
            'order-id-text board-cell-tooltip-trigger' +
            (sectionName === 'Previsto para hoje' && ordem.RoteiroHoje === 1
              ? ' order-id-text--roteiro-hoje'
              : '')
          }
          onMouseEnter={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            let text =
              userShowOSConfig === 1
                ? `Número da OS: ${ordem.TB02115_CODIGO || ordem.id}\nNúmero de série: ${
                    ordem.TB02115_NUMSERIE || ordem.numeroSerie || 'N/A'
                  }`
                : `Número de patrimônio: ${ordem.TB02112_PAT || ordem.patrimonio || 'N/A'}\nNúmero de série: ${
                    ordem.TB02115_NUMSERIE || ordem.numeroSerie || 'N/A'
                  }`;
            if (sectionName === 'Previsto para hoje' && ordem.RoteiroHoje === 1) {
              text += '\nRoteirizada para hoje';
            }
            if (isAguardando) text += '\nAguardando atendimento';
            if (isAssinatura) text += '\nAguardando assinatura';
            onCellTooltip?.({
              text,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }}
          onMouseLeave={() => onCellTooltip?.(null)}
        >
          {userShowOSConfig === 1
            ? ordem.TB02112_PAT || ordem.patrimonio || ordem.id
            : ordem.id}
        </span>
        {nativeCycle ? (
          <span
            className="technician-order-cycle board-cell-tooltip-trigger"
            aria-label={`Ciclo ${nativeCycle}`}
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              onCellTooltip?.({
                text: `OS de retorno, no ciclo ${nativeCycle}`,
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onCellTooltip?.(null)}
          >
            /{nativeCycle}
          </span>
        ) : (
          <OrderRetornoIcon
            ordem={ordem}
            codstatusRetorno={codstatusRetorno}
            codstatusPedidoVinculadoDisponivel={codstatusPedidoVinculadoDisponivel}
            extraRetornoStatuses={extraRetornoStatuses}
            onTooltip={onCellTooltip}
          />
        )}
        {ordem.pedidoVinculado ? (
          <span
            className="board-cell-tooltip-trigger order-pedido-vinc-icon-wrap"
            style={{ display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={(e) => {
              const text = `Pedido vinculado: ${ordem.pedidoVinculado}`;
              const r = e.currentTarget.getBoundingClientRect();
              onCellTooltip?.({
                text,
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onCellTooltip?.(null)}
          >
            <i className="bi bi-box-fill pedido-vinculado-icon" aria-hidden />
          </span>
        ) : null}
      </div>
      <div
        className="technician-order-cell technician-order-cliente board-cell-tooltip-trigger"
        onMouseEnter={(e) => {
          const tip = getBoardClienteTooltipNome(ordem);
          if (tip) {
            const r = e.currentTarget.getBoundingClientRect();
            onCellTooltip?.({
              text: tip,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }
        }}
        onMouseLeave={() => onCellTooltip?.(null)}
      >
        {getBoardClienteExibicao(ordem)}
      </div>
      {equipamentoNode}
    </div>
  );
}

const TechnicianOrderRowMemo = memo(TechnicianOrderRow, areBoardOrderRowPropsEqual);
TechnicianOrderRowMemo.displayName = 'TechnicianOrderRow';

export { TechnicianOrderRowMemo as TechnicianOrderRow };
export default TechnicianOrderRowMemo;
